import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { changeMailService } from '../../actions/orderActions';
import { ReactComponent as Success } from '../../assets/checked.svg';
import { domainBundlesSelector } from '../../selectors/bundleSelectors';
import CustomButton from '../Common/Button/Button';
import styles from './ChangeMailService.module.scss';
import { MAIL_SERVICE_TYPES, BUNDLE_STATUSES } from '../../helpers/constants';
import { Bundle } from '../../types/bundle.types';

const { PARTNER_HOSTING, FLOCK_CLOUD } = MAIL_SERVICE_TYPES;

type ChangeMailServiceProps = {
  userId: number;
  mailService: keyof typeof MAIL_SERVICE_TYPES; // 'partner_hosting' | 'flock_cloud'
  domainName: string;
  closeModal: (newBundleId: number | null) => void;
};

type ChangeMailServiceResponse = {
  toggledEntities: {
    toggledBundles: ToggledBundles;
  };
};
export type ToggledBundle = Pick<
  Bundle,
  'domainName' | 'id' | 'createdAt' | 'status'
>;

type ToggledBundles = ToggledBundle[];

const ChangeMailService = ({
  userId,
  mailService,
  domainName,
  closeModal,
}: ChangeMailServiceProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // Redux state
  const customerBundles = useSelector(domainBundlesSelector);

  const [toggledBundles, setToggledBundles] = useState<ToggledBundles>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isMailServiceChangeInProgress, setIsMailServiceChangeInProgress] =
    useState(false);

  const resetStates = () => {
    setToggledBundles([]);
    setError(null);
    setIsSuccess(false);
  };

  // Switch between partner_hosting ↔ flock_cloud
  const handleButtonClick = useCallback(async () => {
    const desiredMailService =
      mailService === PARTNER_HOSTING ? FLOCK_CLOUD : PARTNER_HOSTING;

    resetStates();

    try {
      setIsMailServiceChangeInProgress(true);
      const response: ChangeMailServiceResponse = (await dispatch(
        changeMailService({ userId, mailService: desiredMailService })
      )) as any;

      if (response?.toggledEntities?.toggledBundles) {
        setToggledBundles(response.toggledEntities.toggledBundles);
        setIsSuccess(true);
      }
    } catch (err) {
      console.error('Error changing mail service:');
      const errorMessage =
        err instanceof Error ? err.message : JSON.stringify(err);
      setError(errorMessage);
    } finally {
      setIsMailServiceChangeInProgress(false);
    }
  }, [dispatch, mailService, userId]);

  const moveMailServiceText =
    mailService === PARTNER_HOSTING ? 'Flock Cloud' : 'cPanel';

  const shouldShowError = error && !isMailServiceChangeInProgress;

  // --- Success view ---
  if (isSuccess) {
    if (!toggledBundles?.length) {
      return null;
    }

    const match = toggledBundles.find((d) => d.domainName === domainName);
    if (!match) return null;

    const newBundleId = match.id;

    return (
      <div className={styles.configureTrialSuccess}>
        <Success />
        <div className={styles.title}>
          <FormattedMessage id="congratulations" />
        </div>
        <div>
          <FormattedMessage
            id="domains-moved-to-mail-service"
            values={{ mailService: moveMailServiceText }}
          />
        </div>
        <CustomButton
          type="primary"
          className={styles.successCloseBtn}
          onClick={() => closeModal(newBundleId)}
        >
          <FormattedMessage id="close" />
        </CustomButton>
      </div>
    );
  }

  // --- Confirmation modal ---
  const modalHeading = intl.formatMessage(
    { id: 'move-customer-to-mail-service' },
    { mailService: moveMailServiceText }
  );

  const modalMessage = intl.formatMessage(
    { id: 'move-action-modal-message-domains-fetched' },
    { mailService: moveMailServiceText }
  );

  return (
    <div className="delete-order-content">
      <span className="delete-orders-header">{modalHeading}</span>
      {shouldShowError && <div className="delete-order-error">{error}</div>}
      <div className="delete-orders-message">
        {modalMessage}
        <div className="order-modal-list">
          {customerBundles
            .filter((bundle: any) => bundle.status !== BUNDLE_STATUSES.DELETED)
            .map((bundle: any) => (
              <div key={bundle.domainName}>- {bundle.domainName}</div>
            ))}
        </div>
        <div className="delete-orders-note">
          <FormattedMessage id="change-mail-service-note" />
        </div>
      </div>
      <div className={styles.changeMailServiceButtonsWrapper}>
        <CustomButton
          onClick={handleButtonClick}
          disabled={isMailServiceChangeInProgress}
          isLoading={isMailServiceChangeInProgress}
        >
          <FormattedMessage id="move-button-text" />
        </CustomButton>
        <CustomButton onClick={() => closeModal(null)} type={'secondary'}>
          <FormattedMessage id="cancel" />
        </CustomButton>
      </div>
    </div>
  );
};

export default ChangeMailService;
